import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { User, Mail, Building2, Calendar, Tag, MoreVertical } from 'lucide-react'
import { ClickToCallButton } from '@/components/ClickToCallButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Lead } from '@/types'
import { formatDate } from '@/lib/utils'

interface MobileLeadCardProps {
  lead: Lead
  isSelected: boolean
  onToggleSelect: (id: string) => void
  onEdit?: (lead: Lead) => void
  onDelete?: (lead: Lead) => void
}

export function MobileLeadCard({
  lead,
  isSelected,
  onToggleSelect,
  onEdit,
  onDelete,
}: MobileLeadCardProps) {
  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'new':
        return 'default'
      case 'working':
        return 'secondary'
      case 'qualified':
        return 'success'
      case 'unqualified':
        return 'destructive'
      default:
        return 'outline'
    }
  }

  return (
    <Card className="relative">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect(lead.id)}
              aria-label={`Select ${lead.name}`}
              className="mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <Link
                to={`/leads/${lead.id}`}
                className="font-semibold text-base hover:underline focus:underline focus:outline-none"
              >
                {lead.name}
              </Link>
              {lead.company && <p className="text-sm text-muted-foreground mt-1">{lead.company}</p>}
              <div className="flex items-center gap-2 mt-1">
                {lead.status && (
                  <Badge variant={getStatusColor(lead.status)} className="text-xs">
                    {lead.status}
                  </Badge>
                )}
                {lead.rating && lead.rating === 'Hot' && (
                  <Badge variant="destructive" className="text-xs">
                    Hot
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={`Actions for ${lead.name}`}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/leads/${lead.id}`}>View Details</Link>
              </DropdownMenuItem>
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(lead)}>Edit Lead</DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(lead)}
                  className="text-destructive focus:text-destructive"
                >
                  Delete Lead
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {lead.title && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <User className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{lead.title}</span>
          </div>
        )}

        {lead.email && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Mail className="h-4 w-4 flex-shrink-0" />
            <a
              href={`mailto:${lead.email}`}
              className="truncate hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {lead.email}
            </a>
          </div>
        )}

        {lead.phone && (
          <div
            className="flex items-center gap-2 text-muted-foreground"
            onClick={(e) => e.stopPropagation()}
          >
            <ClickToCallButton
              phoneNumber={lead.phone}
              whoId={lead.id}
              whoType="Lead"
              whoName={`${lead.first_name} ${lead.last_name}`}
            />
          </div>
        )}

        {lead.lead_source && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Tag className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{lead.lead_source}</span>
          </div>
        )}

        {lead.industry && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Building2 className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{lead.industry}</span>
          </div>
        )}

        {lead.created_date && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4 flex-shrink-0" />
            <span className="text-xs">Created {formatDate(lead.created_date)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface MobileLeadListProps {
  leads: Lead[]
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  onEdit?: (lead: Lead) => void
  onDelete?: (lead: Lead) => void
}

export function MobileLeadList({
  leads,
  selectedIds,
  onToggleSelect,
  onEdit,
  onDelete,
}: MobileLeadListProps) {
  return (
    <div className="space-y-3">
      {leads.map((lead) => (
        <MobileLeadCard
          key={lead.id}
          lead={lead}
          isSelected={selectedIds.has(lead.id)}
          onToggleSelect={onToggleSelect}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}
